import { useEffect, useRef, useState } from 'react'
import Icon, { type IconName } from '../components/Icon'
import { categoryIcon } from './categories'
import { worx, type Category, type Profile as ProfileShape } from './client'
import { categoryShort, tw } from './strings'
import { done } from './feel'

const ARRANGEMENTS = ['full_time', 'part_time', 'temporary', 'seasonal', 'gig'] as const
const TRANSPORT = ['own', 'shared', 'public', 'none'] as const
const PAY_UNITS = ['hour', 'day', 'salary', 'contract', 'tipped'] as const

/** How you get to work, as a picture. The last one is honest, not decorative. */
const TRANSPORT_ICONS: Record<(typeof TRANSPORT)[number], IconName> = {
  own: 'car',
  shared: 'people',
  public: 'route',
  none: 'alert',
}

const EMPTY: ProfileShape = {
  headline: null, summary: null, photoId: null, resumeId: null, availability: null,
  arrangements: [], transport: null, payMin: null, payMax: null, payUnit: 'hour',
  discoverable: true, complete: false, categories: [], skills: [], updatedAt: null,
}

/**
 * The worker profile — the only form in the app a worker ever fills in.
 *
 * It is one screen and one save, matching the API's PUT: a half-saved profile
 * is a state nothing should be able to produce, and multi-step wizards on a
 * phone are where people abandon.
 *
 * ── What is required, and what visibly is not ───────────────────────────────
 *
 * Three fields gate applying: a headline, availability, and one category. They
 * are marked. Everything else — summary, skills, pay, and above all the résumé
 * — is optional and SAYS SO on the label, because the promise is that you do
 * not need a résumé to use this app and a field that merely happens not to be
 * validated does not communicate that.
 *
 * ── Three cards, not eleven fields ──────────────────────────────────────────
 *
 * It was a flat column: eleven controls, every one full width, every one the
 * same weight, in the same hairline boxes. Nothing told you how much was left
 * or what any of it was for. The same fields are now three cards — who you
 * are, what work you want, the practical part — which is both the order
 * somebody thinks in and the order the matcher cares about. The cards are the
 * app's own card, so this screen finally looks like the rest of it.
 *
 * ── Why the choices are grids ───────────────────────────────────────────────
 *
 * Fourteen categories as wrapping pills gave rows of two with a hole on the
 * right and "Gig" orphaned on a line of its own — the ragged edge WAS the
 * unbalanced feeling. A grid has one column edge, so every row ends in the
 * same place whatever the labels say, in either language.
 */
export default function Profile({ onSaved, focusRequest = 0, returnToApplication = false, active = true }: {
  onSaved?: (p: ProfileShape) => void
  focusRequest?: number
  returnToApplication?: boolean
  active?: boolean
}) {
  const [form, setForm] = useState<ProfileShape>(EMPTY)
  const [categories, setCategories] = useState<Category[]>([])
  const [skillText, setSkillText] = useState('')
  const [state, setState] = useState<'loading' | 'ready' | 'saving' | 'saved'>('loading')
  const [error, setError] = useState<string | null>(null)
  const formRef = useRef<HTMLFormElement>(null)
  const focusedRequest = useRef(0)

  function focusMissing() {
    const field = !form.headline?.trim() ? 'headline'
      : !form.availability?.trim() ? 'availability'
      : !form.categories.length ? 'categories' : 'save'
    const target = formRef.current?.querySelector<HTMLElement>(`[data-profile-field="${field}"]`)
    target?.focus({ preventScroll: true })
    target?.scrollIntoView({ block: 'center' })
  }

  useEffect(() => {
    if (!active || state === 'loading' || !focusRequest || focusedRequest.current === focusRequest) return
    focusedRequest.current = focusRequest
    focusMissing()
  })

  useEffect(() => {
    // iPad's keyboard changes the visual viewport after focus. Re-center the
    // active field then, rather than leaving it behind the keyboard/tab bar.
    const reveal = () => {
      const target = document.activeElement
      if (target instanceof HTMLElement && !formRef.current?.closest('[hidden]') && formRef.current?.contains(target)) {
        target.scrollIntoView({ block: 'center' })
      }
    }
    window.visualViewport?.addEventListener('resize', reveal)
    return () => window.visualViewport?.removeEventListener('resize', reveal)
  }, [])

  useEffect(() => {
    void (async () => {
      const [p, c] = await Promise.all([
        worx.profile().catch(() => ({ profile: null })),
        worx.categories().catch(() => ({ categories: [] })),
      ])
      if (p.profile) {
        setForm(p.profile)
        setSkillText(p.profile.skills.join(', '))
      }
      setCategories(c.categories)
      setState('ready')
    })()
  }, [])

  const set = <K extends keyof ProfileShape>(key: K, value: ProfileShape[K]) => {
    setForm((f) => ({ ...f, [key]: value }))
    // Any edit clears the "Saved." confirmation. Leaving it up while the form
    // has unsaved changes is the message telling a lie.
    setState((s) => (s === 'saved' ? 'ready' : s))
  }

  const toggle = (key: 'arrangements' | 'categories', value: string) => {
    const current = form[key]
    set(key, current.includes(value) ? current.filter((v) => v !== value) : [...current, value])
  }

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setState('saving')
    setError(null)
    try {
      const { profile } = await worx.saveProfile({
        ...form,
        // Split here rather than on every keystroke: re-parsing while someone
        // is mid-word makes the field fight them.
        skills: skillText.split(',').map((s) => s.trim()).filter(Boolean),
      })
      setForm(profile)
      setSkillText(profile.skills.join(', '))
      setState('saved')
      done()
      if (!profile.complete) focusMissing()
      onSaved?.(profile)
    } catch (err) {
      setError(err instanceof Error ? err.message : tw('w.error.save'))
      setState('ready')
    }
  }

  if (state === 'loading') return <p className="muted">…</p>

  return (
    <form ref={formRef} className="w-profile" onSubmit={save} onInvalidCapture={(e) => (e.target as HTMLElement).scrollIntoView({ block: 'center' })}>
      <header className="w-profile-head">
        <h2>{tw('w.profile.title')}</h2>
        <p className="muted">{tw('w.profile.intro')}</p>
      </header>

      {!form.complete && <div className="w-guidance" role="status">
        <p>{tw('w.profile.incomplete')}</p>
        <button type="button" onClick={focusMissing}>{tw('w.guidance.missingFields')}</button>
      </div>}

      <section className="card w-fields">
        <h3>{tw('w.profile.aboutYou')}</h3>

        <label>
          <span className="w-label">{tw('w.profile.headline')} <span className="w-required">*</span></span>
          <input
            data-profile-field="headline"
            value={form.headline ?? ''}
            onChange={(e) => set('headline', e.target.value)}
            placeholder={tw('w.profile.headlinePlaceholder')}
            maxLength={120}
            required
          />
        </label>

        <label>
          <span className="w-label">{tw('w.profile.availability')} <span className="w-required">*</span></span>
          <textarea
            data-profile-field="availability"
            value={form.availability ?? ''}
            onChange={(e) => set('availability', e.target.value)}
            placeholder={tw('w.profile.availabilityPlaceholder')}
            rows={2}
            required
          />
        </label>

        <label>
          {tw('w.profile.summary')}
          <textarea
            value={form.summary ?? ''}
            onChange={(e) => set('summary', e.target.value)}
            rows={4}
            maxLength={2000}
          />
        </label>
      </section>

      <section className="card w-fields">
        <h3>{tw('w.profile.theWork')}</h3>

        <fieldset data-profile-field="categories" tabIndex={-1}>
          <legend>
            <span className="w-label">{tw('w.profile.categories')} <span className="w-required">*</span></span>
          </legend>
          <p className="muted small w-hint">{tw('w.profile.categoriesHint')}</p>
          {/*
            The icon is decorative — `Icon` marks it aria-hidden and the label
            beside it is the real name. A screen reader reads "Food, pressed",
            not "picture of cutlery".
          */}
          <div className="w-cats">
            {categories.map((c) => {
              const on = form.categories.includes(c.id)
              return (
                <button
                  type="button"
                  key={c.id}
                  className={on ? 'w-cat on' : 'w-cat'}
                  aria-pressed={on}
                  onClick={() => toggle('categories', c.id)}
                >
                  <Icon name={categoryIcon(c.id)} size={26} />
                  <span>{categoryShort(c)}</span>
                </button>
              )
            })}
          </div>
        </fieldset>

        <fieldset>
          <legend>{tw('w.profile.arrangements')}</legend>
          <div className="w-opts">
            {ARRANGEMENTS.map((a) => {
              const on = form.arrangements.includes(a)
              return (
                <button
                  type="button"
                  key={a}
                  className={on ? 'w-opt on' : 'w-opt'}
                  aria-pressed={on}
                  onClick={() => toggle('arrangements', a)}
                >
                  {tw(`w.arr.${a}`)}
                </button>
              )
            })}
          </div>
        </fieldset>
      </section>

      <section className="card w-fields">
        <h3>{tw('w.profile.practical')}</h3>

        <label>
          {tw('w.profile.skills')}
          <input value={skillText} onChange={(e) => setSkillText(e.target.value)} />
          <span className="muted small">{tw('w.profile.skillsHint')}</span>
        </label>

        {/*
          Three captioned fields on one row, not two small boxes with a dash
          and then a full-width select underneath. "16 – 24 per hour" is one
          sentence and it now looks like one.
        */}
        <fieldset>
          <legend>{tw('w.profile.pay')}</legend>
          <div className="w-pay-row">
            <label className="w-pay-field">
              <span className="muted small">{tw('w.profile.payFrom')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={form.payMin ?? ''}
                onChange={(e) => set('payMin', e.target.value === '' ? null : Number(e.target.value))}
              />
            </label>
            <label className="w-pay-field">
              <span className="muted small">{tw('w.profile.payTo')}</span>
              <input
                type="number"
                inputMode="numeric"
                min={0}
                value={form.payMax ?? ''}
                onChange={(e) => set('payMax', e.target.value === '' ? null : Number(e.target.value))}
              />
            </label>
            <label className="w-pay-field w-pay-unit">
              <span className="muted small">{tw('w.profile.payPer')}</span>
              <select value={form.payUnit ?? 'hour'} onChange={(e) => set('payUnit', e.target.value)}>
                {PAY_UNITS.map((u) => (
                  <option key={u} value={u}>{tw(`w.pay.${u}`)}</option>
                ))}
              </select>
            </label>
          </div>
        </fieldset>

        {/*
          Four buttons rather than a select. Transport is the question most
          likely to decide whether a job on the other side of the island is
          possible at all, and a full-width dropdown reading "—" hid that
          behind a tap. Four answers fit; a dropdown is for lists that do not.
        */}
        <fieldset>
          <legend>{tw('w.profile.transport')}</legend>
          <div className="w-opts w-opts-2">
            {TRANSPORT.map((x) => {
              const on = form.transport === x
              return (
                <button
                  type="button"
                  key={x}
                  className={on ? 'w-opt on' : 'w-opt'}
                  aria-pressed={on}
                  // Pressing the chosen one again clears it — the select had a
                  // "—" option and losing it must not mean losing the ability
                  // to say nothing.
                  onClick={() => set('transport', on ? null : x)}
                  title={tw(`w.transport.${x}`)}
                >
                  <Icon name={TRANSPORT_ICONS[x]} size={20} />
                  <span>{tw(`w.transport.short.${x}`)}</span>
                </button>
              )
            })}
          </div>
        </fieldset>

        <label className="w-check">
          <input
            type="checkbox"
            checked={form.discoverable}
            onChange={(e) => set('discoverable', e.target.checked)}
          />
          <span>
            {tw('w.profile.discoverable')}
            <span className="muted small"> — {tw('w.profile.discoverableHint')}</span>
          </span>
        </label>
      </section>

      {error && <p className="error">{error}</p>}

      <div className="w-profile-save">
        <button data-profile-field="save" className="primary" type="submit" disabled={state === 'saving'}>
          {state === 'saving' ? '…' : tw(returnToApplication ? 'w.guidance.saveReturn' : 'w.profile.save')}
        </button>
        {state === 'saved' && <p className="w-ok">{tw('w.profile.saved')}</p>}
      </div>
    </form>
  )
}
